import { useState, type FC, type ReactNode } from 'react'

import { Button, StyledText } from '@skytech/ui'
import { Badge } from '@skytech/ui/Badge'
import AccountIcon from 'mdi-react/AccountIcon'
import CancelIcon from 'mdi-react/CancelIcon'
import CartOutlineIcon from 'mdi-react/CartOutlineIcon'
import CheckCircleOutlineIcon from 'mdi-react/CheckCircleOutlineIcon'
import CloseCircleOutlineIcon from 'mdi-react/CloseCircleOutlineIcon'
import EyeOffOutlineIcon from 'mdi-react/EyeOffOutlineIcon'
import EyeOutlineIcon from 'mdi-react/EyeOutlineIcon'
import MinusCircleOutlineIcon from 'mdi-react/MinusCircleOutlineIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { LocalDate } from '@/components/LocalDate'
import RouterLink from '@/components/RouterLink'
import { useLocalDateTime } from '@/hooks/use-local-date-time'
import type { EndOfLeaseWorkflowHistoryOriginal } from '@/resources/leasing/types'
import { APP_ROUTES } from '@/util/app-routes'

import { getDaysBeforeEndOfLease } from './helper'

type ItemStatus = 'done' | 'failed' | 'cancelled' | 'skipped'

const STATUS_DOT: Record<ItemStatus, ReactNode> = {
  done: <CheckCircleOutlineIcon />,
  failed: <CloseCircleOutlineIcon />,
  cancelled: <CancelIcon />,
  skipped: <MinusCircleOutlineIcon />,
}

export const EndOfLeaseHistory: FC<{ history: EndOfLeaseWorkflowHistoryOriginal[] }> = ({
  history,
}) => {
  const [showSkipped, setShowSkipped] = useState(false)
  const isAnySkipped = history.some(item => item.isSkipped)
  const filteredHistory =
    showSkipped || !isAnySkipped ? history : history.filter(item => !item.isSkipped)

  return (
    <section className="shadow-basic m-2 flex flex-1 basis-[30rem] flex-col gap-4 rounded-md bg-white p-4">
      <div className="flex w-full flex-row items-center justify-between">
        <div className="flex flex-row items-center">
          <StyledText colorMode="black" size="large">
            <Translation id="End of lease history" />
          </StyledText>

          <Badge type="info" size="small" className="ml-2 h-fit">
            <Translation
              id="{count} {count, plural, one {event} other {events}}"
              count={String(filteredHistory.length)}
            />
          </Badge>
        </div>

        <div>
          {isAnySkipped && (
            <Button
              size="extraSmall"
              className="m-0 px-2 py-0"
              Icon={showSkipped ? <EyeOutlineIcon /> : <EyeOffOutlineIcon />}
              onClick={() => setShowSkipped(!showSkipped)}
            >
              {showSkipped ? <Translation id="Hide skipped" /> : <Translation id="Show skipped" />}
            </Button>
          )}
        </div>
      </div>

      <div>
        {filteredHistory.map((item, index) => (
          <TimelineItem key={item.id} item={item} isLast={index === filteredHistory.length - 1} />
        ))}
      </div>
    </section>
  )
}

const TimelineItem: FC<{ item: EndOfLeaseWorkflowHistoryOriginal; isLast: boolean }> = ({
  item,
  isLast,
}) => {
  const { formatTime } = useLocalDateTime()

  const isSkipped = Boolean(item.isSkipped)
  const status: ItemStatus = isSkipped
    ? 'skipped'
    : item.failedDatetime
      ? 'failed'
      : item.cancelledDatetime
        ? 'cancelled'
        : 'done'

  const numDaysBeforeEndOfLease = getDaysBeforeEndOfLease(
    item.endOfLeaseAsset?.endOfLeaseDate,
    item.eventDatetime
  )
  const daysBeforeEndOfLease = numDaysBeforeEndOfLease ? String(numDaysBeforeEndOfLease) : null

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <TimelineDot status={status} />
        {!isLast && <div className="w-px flex-1 border border-solid border-gray-200" />}
      </div>

      <div
        className={twJoin(
          'flex min-w-0 flex-1 flex-row items-start justify-between gap-4 pb-6',
          isLast && 'pb-0'
        )}
      >
        <div className="flex flex-col gap-1">
          <p
            className={twJoin(
              'm-0 text-sm font-medium leading-tight',
              isSkipped ? 'text-gray-600' : 'text-gray-900'
            )}
          >
            {item.event} {item.endofleaseEventType && `- ${item.endofleaseEventType}`}
          </p>

          {(item.eventDatetime || item.endofleaseEventType) && (
            <div className="flex flex-row items-center gap-2">
              {item.eventDatetime && (
                <span className="m-0 mt-0.5 text-xs text-gray-600">
                  {formatTime({ date: item.eventDatetime })}
                </span>
              )}

              {daysBeforeEndOfLease && (
                <Badge type="info" size="small" className="ml-2 h-fit text-xs">
                  <span>{daysBeforeEndOfLease}</span>
                  <Translation id="d before end" />
                </Badge>
              )}

              <ItemStatusBadge
                isSkipped={isSkipped}
                failedDatetime={item.failedDatetime}
                cancelledDatetime={item.cancelledDatetime}
              />
            </div>
          )}

          <ItemChildren
            costObjectId={item.costObjectId}
            isLocation={item.costObjectType === 'L'}
            ecomOrderId={item.ecomOrderId}
            eventComment={item.eventComment}
            isSkipped={isSkipped}
          />
        </div>

        {Boolean(item.isAppEvent || item.isManagerEvent) && (
          <div>
            {Boolean(item.isAppEvent) && (
              <Badge type="info" size="regular" className="h-fit">
                <Translation id="App" />
              </Badge>
            )}
            {Boolean(item.isManagerEvent) && (
              <Badge type="info" size="regular" className="h-fit">
                <Translation id="Manager" />
              </Badge>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const TimelineDot: FC<{ status: ItemStatus }> = ({ status }) => {
  const icon = STATUS_DOT[status]
  return (
    <Badge
      type={status === 'failed' ? 'negative' : status === 'cancelled' ? 'neutral' : 'positive'}
      className={twJoin(
        'm-0 flex h-9 w-9 flex-shrink-0 items-center rounded-full p-0',
        status === 'skipped' && 'bg-gray-200 text-gray-500'
      )}
      textWrapperClassName="p-0 text-center mx-auto"
    >
      {icon}
    </Badge>
  )
}

const ItemStatusBadge: FC<{
  isSkipped: boolean
  failedDatetime?: Date | null
  cancelledDatetime?: Date | null
}> = ({ isSkipped, failedDatetime, cancelledDatetime }) => {
  if (isSkipped) {
    return (
      <Badge type="info" size="small" className="ml-2 h-fit bg-gray-200 text-xs text-gray-600">
        <Translation id="Skipped" />
      </Badge>
    )
  }

  if (failedDatetime) {
    return (
      <Badge type="negative" size="small" className="ml-2 h-fit text-xs">
        <Translation id="Failed" className="mr-1" /> <LocalDate date={failedDatetime} />
      </Badge>
    )
  }

  if (cancelledDatetime) {
    return (
      <Badge type="neutral" size="small" className="ml-2 h-fit text-xs">
        <Translation id="Cancelled" className="mr-1" /> <LocalDate date={cancelledDatetime} />
      </Badge>
    )
  }
  return null
}

const ItemChildren: FC<{
  isSkipped: boolean
  eventComment?: string | null
  costObjectId?: number | null
  isLocation?: boolean
  ecomOrderId?: number | null
}> = ({ eventComment, costObjectId, isLocation, ecomOrderId, isSkipped }) => {
  if (!eventComment && !costObjectId && !ecomOrderId) return null

  return (
    <div className="mt-1">
      {(ecomOrderId || costObjectId) && (
        <div className="flex flex-row flex-wrap items-center gap-2">
          {ecomOrderId &&
            (isSkipped ? (
              <ItemChildrenBadge isHovered={false} title="Order" id={ecomOrderId} />
            ) : (
              <RouterLink
                to={APP_ROUTES.HARDWARE_ECOM_POLICY_ORDER.replace(
                  ':ecomPolicyOrderId',
                  String(ecomOrderId)
                )}
                hasUnderline={false}
              >
                <ItemChildrenBadge isHovered title="Order" id={ecomOrderId} />
              </RouterLink>
            ))}
          {costObjectId && (
            <EmployeeBadgeLink
              costObjectId={costObjectId}
              isLocation={isLocation}
              isSkipped={isSkipped}
            />
          )}
        </div>
      )}

      {eventComment && (
        <p className="m-0 mt-2 border-l-4 border-solid border-gray-200 border-y-transparent border-r-transparent pl-2 text-sm text-gray-600">
          {eventComment}
        </p>
      )}
    </div>
  )
}

const EmployeeBadgeLink: FC<{ costObjectId: number; isLocation?: boolean; isSkipped: boolean }> = ({
  costObjectId,
  isLocation,
  isSkipped,
}) => {
  if (isSkipped) {
    return <ItemChildrenBadge isHovered={false} title="Employee" id={costObjectId} />
  }

  return (
    <>
      {isLocation ? (
        <RouterLink
          to={APP_ROUTES.HARDWARE_ASSET_LOCATION.replace(':assetLocationId', String(costObjectId))}
          hasUnderline={false}
        >
          <ItemChildrenBadge isHovered title="Employee" id={costObjectId} />
        </RouterLink>
      ) : (
        <RouterLink
          to={APP_ROUTES.EMPLOYEE.replace(':costObjectId', String(costObjectId))}
          hasUnderline={false}
        >
          <ItemChildrenBadge isHovered title="Employee" id={costObjectId} />
        </RouterLink>
      )}
    </>
  )
}

const ItemChildrenBadge: FC<{ id: number; title: string; isHovered: boolean }> = ({
  id,
  title,
  isHovered,
}) => (
  <Badge
    type="outline"
    size="small"
    className={twJoin(
      'h-fit text-xs',
      isHovered ? 'hover:shadow-basic hover:bg-gray-50' : 'text-gray border-gray-200'
    )}
  >
    <div className="flex flex-row items-center gap-1">
      {title === 'Employee' && <AccountIcon />}
      {title === 'Order' && <CartOutlineIcon />}
      <Translation id={title} />
      {' #'}
      {id}
    </div>
  </Badge>
)
